import type { DeliveryZone } from '@choco-admin/types';
import {
  Button,
  Checkbox,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Spinner,
} from '@heroui/react';
import React, { useEffect, useMemo, useState } from 'react';
import { FiSearch } from 'react-icons/fi';

import { AlertCard } from '../../../../../../components/AlertCard';
import { useDeliveryZoneLibrary } from '../../../../hooks/useDeliveryZoneLibrary';
import {
  filterLibraryZonesByName,
  LIBRARY_COUNTRIES,
  resolveLibraryLocale,
  toDeliveryZonesFromLibrary,
} from '../../../../utils/deliveryZoneLibrary';

interface DeliveryZoneLibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The distributor's locale, used to pick the starting country. */
  supplierLocale?: string | null;
  /** Areas already on the distributor, so taken names can be skipped. */
  existingZones: DeliveryZone[];
  onAdd: (zones: DeliveryZone[], skippedNames: string[]) => void;
}

/** Rendering thousands of checkboxes janks the modal; the search narrows it. */
const MAX_VISIBLE = 200;

/**
 * Pick ready-made delivery areas instead of drawing them.
 *
 * Legacy admin has had this for years and CS relies on it: picking "Greater
 * London" beats tracing it by hand. The regions come from a per-country file, so
 * the country picker is deliberately independent of the distributor's locale.
 * A distributor in Germany may still deliver in Belgium.
 */
export const DeliveryZoneLibraryModal: React.FC<
  DeliveryZoneLibraryModalProps
> = ({ isOpen, onClose, supplierLocale, existingZones, onAdd }) => {
  const { zones, status, loadCountry } = useDeliveryZoneLibrary();
  const [locale, setLocale] = useState(() =>
    resolveLibraryLocale(supplierLocale)
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedNames, setSelectedNames] = useState<string[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    loadCountry(locale);
  }, [isOpen, locale, loadCountry]);

  // Selections belong to the country they were made in.
  useEffect(() => {
    setSelectedNames([]);
  }, [locale]);

  const matching = useMemo(
    () => filterLibraryZonesByName(searchTerm, zones),
    [searchTerm, zones]
  );
  const visible = matching.slice(0, MAX_VISIBLE);
  const hiddenCount = matching.length - visible.length;

  const handleClose = () => {
    setSearchTerm('');
    setSelectedNames([]);
    onClose();
  };

  const handleAdd = () => {
    const selected = zones.filter((zone) =>
      selectedNames.includes(zone.nameLocalised)
    );
    const { zones: added, skippedNames } = toDeliveryZonesFromLibrary({
      selected,
      existingZones,
    });
    onAdd(added, skippedNames);
    handleClose();
  };

  const toggle = (name: string, isSelected: boolean) => {
    setSelectedNames((current) =>
      isSelected
        ? [...current, name]
        : current.filter((selected) => selected !== name)
    );
  };

  const countryLabel =
    LIBRARY_COUNTRIES.find((country) => country.locale === locale)?.label ??
    locale;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="2xl"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <h2 className="text-large font-semibold">Add a ready-made area</h2>
          <p className="text-small font-normal text-default-500">
            Pick from the areas Choco already has for a country, instead of
            drawing them.
          </p>
        </ModalHeader>
        <ModalBody className="gap-3">
          <Select
            label="Country"
            selectedKeys={[locale]}
            onChange={(event) => {
              if (event.target.value) setLocale(event.target.value);
            }}
            disallowEmptySelection
          >
            {LIBRARY_COUNTRIES.map((country) => (
              <SelectItem key={country.locale}>{country.label}</SelectItem>
            ))}
          </Select>

          <Input
            value={searchTerm}
            onValueChange={setSearchTerm}
            placeholder="Search areas, for example London"
            startContent={<FiSearch className="text-default-400" />}
            isDisabled={status !== 'ready'}
          />

          {status === 'loading' && (
            <div className="flex justify-center py-8">
              <Spinner label={`Loading areas for ${countryLabel}`} />
            </div>
          )}

          {status === 'empty' && (
            <AlertCard
              type="info"
              title={`No ready-made areas for ${countryLabel}`}
              message="Nothing has been prepared for this country yet. Draw the area on the map instead."
            />
          )}

          {status === 'failed' && (
            <AlertCard
              type="warning"
              title="The ready-made areas could not be loaded"
              message="Try again in a moment. If it keeps failing, draw the area on the map instead and report it."
            />
          )}

          {status === 'ready' && (
            <>
              {matching.length === 0 ? (
                <p className="py-6 text-center text-small text-default-500">
                  Nothing matches “{searchTerm}”.
                </p>
              ) : (
                <ul className="space-y-1">
                  {visible.map((zone) => (
                    <li key={`${zone.locale}-${zone.nameLocalised}`}>
                      <Checkbox
                        isSelected={selectedNames.includes(zone.nameLocalised)}
                        onValueChange={(isSelected) =>
                          toggle(zone.nameLocalised, isSelected)
                        }
                      >
                        <span className="text-small">
                          {zone.nameLocalised}
                          {zone.nameEnglish !== zone.nameLocalised && (
                            <span className="ml-2 text-default-500">
                              {zone.nameEnglish}
                            </span>
                          )}
                        </span>
                      </Checkbox>
                    </li>
                  ))}
                </ul>
              )}
              {hiddenCount > 0 && (
                <p className="text-tiny text-default-500">
                  Showing the first {MAX_VISIBLE} of {matching.length} areas.
                  Search to narrow the list.
                </p>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={handleClose}>
            Cancel
          </Button>
          <Button
            color="primary"
            onPress={handleAdd}
            isDisabled={selectedNames.length === 0}
          >
            {selectedNames.length > 0
              ? `Add ${selectedNames.length} area${selectedNames.length === 1 ? '' : 's'}`
              : 'Add areas'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
